<template>
  <transition name="view" appear>
    <div>
      <div class="item-select" v-if="renderView">
        <div v-if="currentView.dropdown && currentView.dropdown.show">
          <RexSearchSelect
            :data="databaseInstances"
            :current="currentDatabaseInstance"
            :objectValueIndex="AppObjectIndex"
            width="14.875rem"
            @onChoose="onInstanceChange"
            :searchPlaceholder="placeholder"
            :boldActive="boldActive"
            id="search-select-database-instance"
          />
        </div>

        <div class="wrapper-cards-list">
          <MSSQLInfoCards
            :cardNames="currentView.cards.template.clusterCardsName"
            :clusterInfo="clusterInfo"
            v-if="showMssqlInfoCards"
          />
          <div v-else class="wrapper-card-elements">
            <div
              v-if="currentView.cards && currentView.cards.template"
              class="wrapper-card"
            >
              <div
                class="card-element"
                v-for="(card, cardIndex) in currentView.cards.template.cardName"
                :key="cardIndex"
                :style="{
                  width: currentView.cards.template.width,
                }"
              >
                <InfoStats
                  class="info-border"
                  :textLabel="card"
                  :textValue="textValue[cardIndex]"
                  :fontSize="fontSize"
                  textValHeight="3rem"
                  lineHeight="1.2rem"
                  :textValColor="
                    textValue[cardIndex] === 'Active'
                      ? 'good'
                      : textValue[cardIndex] === 'Inactive'
                        ? 'poor'
                        : 'fair'
                  "
                />
              </div>
            </div>
            <div
              v-if="
                currentView.storageCards && currentView.storageCards.template
              "
              class="wrapper-card-storage"
            >
              <div
                class="card-element"
                v-for="(item, index) in currentView.storageCards.template
                  .storageCards"
                :key="index"
                :style="{
                  width: item.width,
                }"
              >
                <InfoStats
                  class="info-border"
                  :textLabel="item.label"
                  :textValue="storageData[index]"
                  :fontSize="fontSize"
                  textValHeight="2.8rem"
                />
              </div>
            </div>
          </div>

          <div
            v-if="currentView.progressCard && currentView.progressCard.show"
            class="progress-card"
          >
            <RexProgressCard
              :showProgressTop="showProgressTop"
              :showTotal="showTotal"
              :progressHeaderColor="progressHeaderColor"
              :colorInfo="colorInfo"
              :headerName="this.$t('dbm.hardware.diskUsage')"
              :headerLegends="headerLegends"
              :items="diskData"
              :loading="loading"
              color="#abc0ff"
            />
          </div>
        </div>
        <div
          class="wrapper"
          v-if="currentView.graphs && currentView.graphs.template"
        >
          <div
            class="chart-element"
            v-for="(chartConfig, index) in getGraphCharts"
            :key="index + 'linw' + chartConfig.metricName"
            :style="{ width: chartConfig.width }"
          >
            <GraphBuilder
              :chartConfig="chartConfig"
              :index="index"
              :layoutIndex="0"
            />
          </div>
        </div>
        <div
          v-if="currentView.count && currentView.count.show"
          class="query-threshold-action"
        >
          <RexSearchSelect
            width="12rem"
            :data="dropdownValues"
            :current="currentQueryTimeThreshold"
            :isSearch="false"
            :heading="$t('queryDbTimeThresholdDropdown')"
            @onChoose="onQueryTimeThresholdChange"
            :objectValueIndex="1"
            id="query-threshold-select"
          />
          <DatabaseQueryDetailsPdf
            :headers="headers"
            :fileName="getPdfFileName"
            :queryTimeThreshold="currentQueryTimeThreshold"
            :disabled="isQueryDetailsEmpty"
            :sortCriteria="getSortCriteria"
          />
        </div>
        <div v-if="currentView.table && currentView.table.show">
          <RexTableCompV2
            :loading="loading"
            :headers="headers"
            :items="tableItems"
            :height="tablesHeight"
            :tablewidth="tablesWidth"
            highlight="primary"
            variant="secondary"
            :hideVerticalLine="true"
            :title="this.$t('dbm.connections.sessionTableTitle')"
            :showTitle="showTitle"
          >
            <template
              v-for="header in headers"
              #[`body-${header.key}`]="{ item }"
            >
              <div
                v-if="header.key === 'timestamp' || header.key === 'timeStamp'"
                :key="`${header.key}-${getRandomUniqueId()}`"
              >
                {{
                  $filters.dateformat(
                    parseInt(item[header.key]),
                    timezoneoffset,
                    'DD.MM.YY [at] hh:mm A',
                  )
                }}
              </div>
              <div
                v-else-if="header.key === 'value'"
                :key="`${header.key}-${getRandomUniqueId()}`"
              >
                {{
                  item[header.key]
                    ? formatValue(item[header.key])
                    : item[header.key]
                }}
              </div>
            </template>
            <template
              v-for="header in headers"
              #[`header-${header.key}`]
              :key="header.key"
            >
              <div>
                <RexTableSortField
                  v-if="header.sortable"
                  :name="header.name"
                  @toggleSort="() => toggleSorting(header.key)"
                  :isAsc="isAsc"
                  :isActiveField="
                    sortingField.toLowerCase() === header.key.toLowerCase()
                  "
                />
                <div v-else>
                  {{ header.name }}
                </div>
              </div>
            </template>
          </RexTableCompV2>
          <div
            class="d-flex justify-content-end mt-3 pagination"
            v-if="
              currentSubType.key == PS_QUERYPERFORMANCE ||
              currentSubType.key == ORA_QUERYPERFORMANCE ||
              currentSubType.key == MSSQL_QUERY_PERFORMANCE
            "
          >
            <RexPaginationV3
              @stateChange="(currState) => handleRefresh(currState)"
              :defaultPageNo="currentPage"
              :total="total"
            />
          </div>
        </div>
      </div>
      <div v-if="this.currentDatabaseType.key === CASSANDRA">
        <Cassandra />
      </div>
      <div v-if="this.currentDatabaseType.key === COUCHBASE">
        <Couchbase />
      </div>
      <div v-if="this.currentDatabaseType.key === MYSQL">
        <MySQL />
      </div>
      <div v-if="this.currentDatabaseType.key === MONGODB">
        <MongoDB />
      </div>
    </div>
  </transition>
</template>

<script>
import GraphBuilder from '@/components/Common/GraphBuilder.vue';
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import RexSearchSelect from '@/rex-components/RexSearchSelect.vue';
import InfoStats from '@/rex-components/rex-info-stats.vue';
import { connectionsTableHeaders } from '@/utils/templates/databaseTemplate/ps.connections.table.config.js';
import {
  queryPerformanceTableHeaders,
  QueryThresholdValues,
} from '@/utils/templates/databaseTemplate/ps.queryperformance.table.config';
import { oraQueryPerformanceTableHeaders } from '@/utils/templates/databaseTemplate/ora.queryperformance.table.config';
import { oraConnectionsTableHeaders } from '@/utils/templates/databaseTemplate/ora.connections.table.config';
import { mssqlQueryPerformanceTableHeaders } from '@/utils/templates/databaseTemplate/mssql.queryPerformance.table.config';
import { mssqlConnectionsTableHeaders } from '@/utils/templates/databaseTemplate/mssql.connections.table.config';

import { storageCardsData } from '@/utils/templates/databaseTemplate/cards.config';
import RexProgressCard from '@/rex-components/RexProgressCard.vue';
import RexTableCompV2 from '@/rex-components/rex-TableCompV2.vue';
import { getRandomUniqueId } from '@/utils/helper-function';
import * as types from '@/components/Database/database.types';
import * as constants from '@/utils/templates/databaseTemplate/database.constants';
import Cassandra from '@/components/Database/DatabaseContent/Cassandra.vue';
import Couchbase from '@/components/Database/DatabaseContent/Couchbase.vue';
import MySQL from '@/components/Database/DatabaseContent/MySQL.vue';
import MongoDB from '@/components/Database/DatabaseContent/MongoDB/MongoDB.vue';
import DatabaseQueryDetailsPdf from '@/components/Database/DatabaseContent/DatabaseQueryDetailsPdf.vue';
import RexPaginationV3 from '@/rex-components/RexPaginationV3.vue';
import MSSQLInfoCards from '@/components/Database/DatabaseContent/MSSQL/MSSQLInfoCards.vue';
import RexTableSortField from '@/rex-components/rex-TableSortField.vue';
import databaseCharts from '@/utils/templates/database.charts.template';

export default {
  name: 'DatabaseContent',
  data() {
    return {
      boldActive: true,
      selectInstance: this.$t('selectInstance'),
      AppObjectIndex: 1,
      showProgressTop: true,
      showTotal: false,
      total: 0,
      progressHeaderColor: true,
      colorInfo: false,
      queryItemsApp: [],
      connectionItemsApp: [],
      dropdownValues: QueryThresholdValues,
      currentQueryTimeThreshold: {},
      currentPage: 1,
      pageSize: 10,
      headerLegends: [
        {
          name: this.$t('dbm.progressLegends.progressLegendAvailable'),
          class: 'primary',
        },
        {
          name: this.$t('dbm.progressLegends.progressLegendUsed'),
          class: 'secondary',
        },
      ],
      diskData: [],
      storageCardsData: storageCardsData,
      showcards: true,
      fontSize: '16px',
      loading: false,
      POS_INSTANCE: constants.SUB_TYPES.POS_INSTANCE,
      ORA_INSTANCE: constants.SUB_TYPES.ORA_INSTANCE,
      PS_QUERYPERFORMANCE: constants.SUB_TYPES.PS_QUERYPERFORMANCE,
      ORA_QUERYPERFORMANCE: constants.SUB_TYPES.ORA_QUERYPERFORMANCE,
      MSSQL_QUERY_PERFORMANCE: constants.SUB_TYPES.MSSQL_QUERY_PERFORMANCE,
      CASSANDRA: types.DatabaseTypes.ca,
      COUCHBASE: types.DatabaseTypes.cb,
      MYSQL: types.DatabaseTypes.my,
      MONGODB: types.DatabaseTypes.mg,
      avgCardData: { cpuUsage: 0, memoryUsage: 0 },
      meterAvgMetricName: '',
      storageData: [],
      isAsc: false,
      sortingField: types.timeStamp,
    };
  },
  components: {
    GraphBuilder,
    RexSearchSelect,
    InfoStats,
    RexProgressCard,
    RexTableCompV2,
    Cassandra,
    // RexPagination,
    RexPaginationV3,
    Couchbase,
    MySQL,
    MongoDB,
    DatabaseQueryDetailsPdf,
    MSSQLInfoCards,
    RexTableSortField,
  },
  computed: {
    ...mapState('globalState/app', ['chartsTemplate']),
    ...mapState('databaseState/databaseCommonState', [
      'currentDatabaseType',
      'currentDatabaseService',
      'sessionDetails',
      'databaseQuery',
      'avgCpu',
      'avgMemory',
    ]),
    ...mapState('databaseState/detail', [
      'currentDatabaseInstance',
      'currentSubType',
      'databaseInstances',
    ]),
    ...mapGetters('globalState/settings', ['getDurationTime', 'intervalTime']),
    ...mapState('globalState/settings', ['timezoneoffset']),
    ...mapGetters('databaseState/databaseCommonState', ['getCurrentDatabase']),
    ...mapGetters('databaseState/databaseCommonState', [
      types.databaseGetters.GET_AVG_CPU,
      types.databaseGetters.GET_AVG_MEMORY,
    ]),
    currentView() {
      if (this.currentDatabaseType && this.currentSubType)
        return this.chartsTemplate[this.currentDatabaseType.key][
          this.currentSubType.key
        ];
      else return {};
    },
    getGraphCharts() {
      return databaseCharts()[this.currentDatabaseType?.key]?.[
        this.currentSubType?.key
      ].graphs?.template;
    },
    renderView() {
      const excludedDBTypes = [
        this.CASSANDRA,
        this.COUCHBASE,
        this.MYSQL,
        this.MONGODB,
      ];
      return (
        this.currentView &&
        !excludedDBTypes.includes(this.currentDatabaseType.key)
      );
    },
    placeholder() {
      if (this.currentSubType) {
        return (
          this.$t('search') + ' ' + this.currentSubType.label.toLowerCase()
        );
      } else return this.$t('searchInstance');
    },
    textValue() {
      if (this.currentDatabaseInstance && this.currentDatabaseInstance.key) {
        let availability;
        const attributes = this.currentDatabaseInstance.attributes;
        const CardDatavalues = this.currentView.cards.template.cardName.map(
          (name) => {
            let CardDatavalue = this.$t('noData');

            switch (name) {
              case types.cardNames.TYPE_OF_OS:
                CardDatavalue = attributes.find(
                  (attr) => attr.name === types.cardValues.OS_TYPE,
                )?.value;
                break;
              case types.cardNames.ARCHITECTURE:
                CardDatavalue = attributes.find(
                  (attr) => attr.name === types.cardValues.OS_ARCH,
                )?.value;
                break;
              case types.cardNames.Host_Name:
                CardDatavalue = attributes.find(
                  (attr) => attr.name === types.cardValues.OS_NAME,
                )?.value;
                break;
              case types.cardNames.Availability:
                availability = attributes.find(
                  (attr) => attr.name === types.cardValues.AVAILABILITY,
                )?.value;
                CardDatavalue =
                  availability == 'true'
                    ? types.cardValues.ACTIVE
                    : types.cardValues.INACTIVE;
                break;
            }

            return CardDatavalue || this.$t('noData');
          },
        );

        return CardDatavalues;
      }
      return this.currentView.cards.template.cardName.map(() =>
        this.$t('noData'),
      );
    },
    clusterInfo() {
      if (this.currentDatabaseInstance && this.currentDatabaseInstance.key) {
        const attributes = this.currentDatabaseInstance.attributes;
        let cardNames = this.currentView.cards.template.clusterCardNamesMap;
        let clusterDatavalues =
          this.currentView.cards.template.clusterCardsName.map((name) => {
            return (
              this.getAttributeValue(attributes, cardNames[name]) ||
              this.$t('noData')
            );
          });
        return clusterDatavalues;
      }
      return this.currentView.cards.template.clusterCardsName.map(() =>
        this.$t('noData'),
      );
    },
    headers() {
      switch (this.currentSubType.key) {
        case constants.SUB_TYPES.PS_QUERYPERFORMANCE:
          return queryPerformanceTableHeaders();
        case constants.SUB_TYPES.PS_CONNECTIONS:
          return connectionsTableHeaders();
        case constants.SUB_TYPES.ORA_QUERYPERFORMANCE:
          return oraQueryPerformanceTableHeaders();
        case constants.SUB_TYPES.ORA_CONNECTIONS:
          return oraConnectionsTableHeaders();
        case constants.SUB_TYPES.MSSQL_QUERY_PERFORMANCE:
          return mssqlQueryPerformanceTableHeaders();
        case constants.SUB_TYPES.MSSQL_CONNECTIONS:
          return mssqlConnectionsTableHeaders();
        default:
          return [];
      }
    },

    tableItems() {
      if (
        this.currentSubType.key == constants.SUB_TYPES.PS_QUERYPERFORMANCE ||
        this.currentSubType.key == constants.SUB_TYPES.ORA_QUERYPERFORMANCE ||
        this.currentSubType.key == constants.SUB_TYPES.MSSQL_QUERY_PERFORMANCE
      ) {
        return this.queryItemsApp;
      } else if (
        this.currentSubType.key == constants.SUB_TYPES.PS_CONNECTIONS ||
        this.currentSubType.key == constants.SUB_TYPES.ORA_CONNECTIONS ||
        this.currentSubType.key == constants.SUB_TYPES.MSSQL_CONNECTIONS
      ) {
        return this.connectionItemsApp;
      } else return [];
    },

    showTitle() {
      if (
        this.currentSubType.key == constants.SUB_TYPES.PS_QUERYPERFORMANCE ||
        this.currentSubType.key == constants.SUB_TYPES.ORA_QUERYPERFORMANCE
      ) {
        return false;
      } else if (
        this.currentSubType.key == constants.SUB_TYPES.PS_CONNECTIONS ||
        this.currentSubType.key == constants.SUB_TYPES.ORA_CONNECTIONS ||
        this.currentSubType.key == constants.SUB_TYPES.MSSQL_CONNECTIONS
      ) {
        return true;
      } else return false;
    },
    showMssqlInfoCards() {
      return this.currentSubType.key == constants.SUB_TYPES.MSSQL_HARDWARE;
    },
    tablesHeight() {
      if (this.currentSubType.key == constants.SUB_TYPES.PS_CONNECTIONS) {
        return '30em';
      } else {
        return '40em';
      }
    },
    tablesWidth() {
      if (this.currentSubType.key == constants.SUB_TYPES.PS_CONNECTIONS) {
        return '96.6%';
      } else {
        return '';
      }
    },
    getPdfFileName() {
      return `${
        this.currentDatabaseType?.label?.toLowerCase() ?? 'db'
      }-query-details`;
    },
    isQueryDetailsEmpty() {
      return this.queryItemsApp?.length === 0;
    },
    getSortCriteria() {
      return [
        {
          field: types.queryTableSortFieldMapping[this.sortingField],
          order: this.isAsc ? constants.ASC : constants.DES,
        },
      ];
    },
  },
  created() {
    this.currentQueryTimeThreshold = this.dropdownValues[2];
    this.diskDetails();
    // this.getAvgData();
    this.avgData();
  },
  methods: {
    ...mapMutations('databaseState/detail', [
      'setCurrentDatabaseInstance',
      'setDatabaseInstances',
    ]),
    ...mapActions('databaseState/detail', ['getDatabaseInstances']),
    ...mapActions('apmState/dashboard', ['GET_ALL_SAMPLE_RECORDS']),
    ...mapActions('databaseState/databaseCommonState', [
      'GET_SESSION_DETAILS',
      'GET_DB_DISK_DATA',
      'GET_ALL_QUERY_RECORDS',
      'getDatabaseGraphData',
      'GET_AVG_DATA',
    ]),
    getRandomUniqueId,
    formatValue(value) {
      const numericValue = Number(value);
      if (Number.isInteger(numericValue)) {
        return numericValue;
      } else {
        return parseFloat(numericValue).toFixed(2);
      }
    },
    formatUserName(label, index) {
      return label.includes('-') ? label.split('-')[index].trim() : label;
    },
    formatResponse(list) {
      return list.map((item) => {
        const userName = item[constants.TABLE_KEYS.LABEL];
        item[constants.TABLE_KEYS.LABEL] = this.formatUserName(userName, 1);
        return {
          _id: `${getRandomUniqueId()}`,
          ...item,
        };
      });
    },
    fetchDatabaseInstances() {
      if (
        this.getCurrentDatabase.key === types.DatabaseTypes.ca ||
        this.getCurrentDatabase.key === types.DatabaseTypes.cb ||
        this.getCurrentDatabase.key === types.DatabaseTypes.my ||
        this.getCurrentDatabase.key === types.DatabaseTypes.mg
      ) {
        return;
      }
      let databaseInstance = {
        duration: this.getDurationTime,
        serviceId: this.currentDatabaseService.key,
      };

      if (this.currentDatabaseService.key) {
        databaseInstance.properties = [
          {
            key: 'instance_type',
            value:
              this.getCurrentDatabase.key === types.DatabaseTypes.pg
                ? constants.SUB_TYPES.POS_INSTANCE
                : this.getCurrentDatabase.key === types.DatabaseTypes.or
                  ? constants.SUB_TYPES.ORA_INSTANCE
                  : this.getCurrentDatabase.key === types.DatabaseTypes.ms
                    ? 'ms-instance'
                    : this.currentSubType.key,
          },
        ];
        this.getDatabaseInstances(databaseInstance);
      }
    },
    onInstanceChange(option) {
      this.setCurrentDatabaseInstance(option);
    },
    handleRefresh(currentState) {
      this.currentPage = currentState.pageNo;
      this.pageSize = currentState.pageSize;
      this.getQueryPerformanceTableData();
    },
    onQueryTimeThresholdChange(data) {
      this.currentQueryTimeThreshold = data;
      this.currentPage = 1;
      this.getQueryPerformanceTableData();
    },
    async diskDetails() {
      if (
        this.currentSubType.key === constants.SUB_TYPES.POS_INSTANCE ||
        this.currentSubType.key === constants.SUB_TYPES.ORA_INSTANCE
      ) {
        await this.getDiskData();
      }
    },

    async avgData() {
      if (
        this.currentSubType.key === constants.SUB_TYPES.POS_INSTANCE ||
        this.currentSubType.key === constants.SUB_TYPES.ORA_INSTANCE
      ) {
        await this.getAvgData();
      }
    },

    async items() {
      if (
        this.currentSubType.key === constants.SUB_TYPES.PS_QUERYPERFORMANCE ||
        this.currentSubType.key === constants.SUB_TYPES.ORA_QUERYPERFORMANCE ||
        this.currentSubType.key === constants.SUB_TYPES.MSSQL_QUERY_PERFORMANCE
      ) {
        await this.getQueryPerformanceTableData();
      } else if (
        this.currentSubType.key === constants.SUB_TYPES.PS_CONNECTIONS ||
        this.currentSubType.key === constants.SUB_TYPES.ORA_CONNECTIONS ||
        this.currentSubType.key === constants.SUB_TYPES.MSSQL_CONNECTIONS
      ) {
        await this.getSessionTableData();
      }
    },
    async getQueryPerformanceTableData() {
      this.loading = true;
      try {
        if (this.currentDatabaseInstance.key) {
          const params = {
            condition: {
              name: types.metricTypes.DBM_QUERIES,
              parentService: this.currentDatabaseInstance.key,
              scope: 'ServiceInstance',
              normal: false,
              searchKeyword: '*',
              queryTimeThreshold: this.currentQueryTimeThreshold.key,
            },
            duration: this.getDurationTime,
            pagination: {
              pageNum: this.currentPage,
              pageSize: this.pageSize,
              needTotal: true,
            },
            sortCriteria: this.getSortCriteria,
          };
          const queryData = await this.GET_ALL_QUERY_RECORDS(params);
          this.queryItemsApp = queryData.records;
          this.total = queryData.total;
        } else {
          this.queryItemsApp = [];
        }
      } catch (error) {
        this.queryItemsApp = [];
      } finally {
        this.loading = false;
      }
    },
    getMemoryAvgMetricName() {
      this.meterAvgMetricName =
        this.currentDatabaseType.key == types.DatabaseTypes.pg
          ? types.metricTypes.METER_POSTGRESS_MEMORY_UASGE
          : this.currentDatabaseType.key == types.DatabaseTypes.or
            ? types.metricOrTypes.METER_ORACLE_MEMORY_UASGE
            : '';
      return this.meterAvgMetricName;
    },
    async getAvgData() {
      this.loading = true;
      try {
        this.avgCardData = await this.GET_AVG_DATA({
          serviceName: this.currentDatabaseService.key,
          duration: this.getDurationTime,
          endpointName: this.currentDatabaseInstance.key,
          cpuAvgMetricName:
            this.currentDatabaseType.key == types.DatabaseTypes.pg
              ? types.metricTypes.METER_POSTGRES_CPU_USAGE
              : this.currentDatabaseType.key == types.DatabaseTypes.or
                ? types.metricOrTypes.METER_ORACLE_CPU_USAGE
                : '',
          memoryAvgMetricName: this.getMemoryAvgMetricName(),
        });
        if (
          this.meterAvgMetricName ==
          types.metricOrTypes.METER_ORACLE_MEMORY_UASGE
        ) {
          this.avgCardData.memoryUsage;
        }
        if (
          this.meterAvgMetricName ==
          types.metricTypes.METER_POSTGRESS_MEMORY_UASGE
        ) {
          this.avgCardData.memoryUsage = (
            this.avgCardData.memoryUsage /
            constants.BYTES_TO_MB_CONVERSION_VALUE
          ).toFixed(2);
        }
        this.storageData = Object.values(this.avgCardData);
        return this.storageData;
      } catch (error) {
        this.storageData = {};
      } finally {
        this.loading = false;
      }
    },
    async getSessionTableData() {
      this.loading = true;
      try {
        if (this.currentDatabaseInstance.key) {
          const params = {
            condition: {
              name:
                this.currentDatabaseType.key == types.DatabaseTypes.or
                  ? types.metricOrTypes.METER_ORACLE_SESSION_DETAILS
                  : this.currentDatabaseType.key == types.DatabaseTypes.pg
                    ? types.metricTypes.METER_POSTGRESS_SESSION_DETAILS
                    : this.currentDatabaseType.key == types.DatabaseTypes.ms
                      ? types.metricMSTypes.METER_SESSION_DETAILS
                      : '',
              entity: {
                scope: 'Endpoint',
                serviceName: this.currentDatabaseService.key,
                endpointName: this.currentDatabaseInstance.key,
                normal: true,
              },
              isLatestValue: true,
            },
            duration: this.getDurationTime,
            labels: [''],
            sortCriteria: [
              {
                field: types.sessionTableSortFieldMapping[this.sortingField],
                order: this.isAsc ? constants.ASC : constants.DES,
              },
            ],
          };
          const sessionDetail = await this.GET_SESSION_DETAILS(params);
          this.connectionItemsApp = sessionDetail;
          if (
            [types.DatabaseTypes.pg, types.DatabaseTypes.ms].includes(
              this.currentDatabaseType.key,
            )
          ) {
            this.formatResponse(this.connectionItemsApp);
          }
        } else {
          this.connectionItemsApp = [];
        }
      } catch (error) {
        this.connectionItemsApp = [];
      } finally {
        this.loading = false;
      }
    },
    async getDiskData() {
      this.loading = true;
      try {
        if (this.currentDatabaseInstance.key) {
          this.diskData = await this.GET_DB_DISK_DATA({
            serviceId: this.currentDatabaseService.key,
            duration: this.getDurationTime,
            endpointName: this.currentDatabaseInstance.key,
            scope: 'Endpoint',
            usedMetricName:
              this.currentDatabaseType.key == types.DatabaseTypes.or
                ? types.metricOrTypes.METER_ORACLE_DISK_SIZE_USED
                : types.metricTypes.METER_POSTGRESS_DISK_SIZE_USED,
            totalMetricName:
              this.currentDatabaseType.key == types.DatabaseTypes.or
                ? types.metricOrTypes.METER_ORACLE_DISK_SIZE_TOTAL
                : types.metricTypes.METER_POSTGRESS_DISK_SIZE_TOTAL,
            availableMetricName:
              this.currentDatabaseType.key == types.DatabaseTypes.or
                ? types.metricOrTypes.METER_ORACLE_DISK_SIZE_AVAILABLE
                : types.metricTypes.METER_POSTGRESS_DISK_SIZE_AVAILABLE,
          });
          return this.diskData;
        } else {
          this.diskData = [];
        }
      } catch (error) {
        this.diskData = [];
      } finally {
        this.loading = false;
      }
    },
    getAttributeValue(attributes, name) {
      const attribute = attributes.find((a) => a.name === name);
      if (name === types.cardValues.AVAILABILITY) {
        const availability = attributes.find(
          (attr) => attr.name === types.cardValues.AVAILABILITY,
        )?.value;

        return availability == 'true'
          ? types.cardValues.ACTIVE
          : types.cardValues.INACTIVE;
      }

      return attribute ? attribute.value : this.$t('noData');
    },
    resetTableData() {
      this.queryItemsApp = [];
      this.loading = true;
      this.total = 0;
      this.currentPage = 1;
    },
    toggleSorting(sortingField) {
      this.isAsc = !this.isAsc;
      this.sortingField = sortingField;
      this.resetTableData();
      this.items();
    },
    setDefaultSortField() {
      this.isAsc = false;
      this.sortingField = types.timeStamp;
      if (
        this.currentSubType.key == constants.SUB_TYPES.PS_QUERYPERFORMANCE ||
        this.currentSubType.key == constants.SUB_TYPES.ORA_QUERYPERFORMANCE ||
        this.currentSubType.key == constants.SUB_TYPES.MSSQL_QUERY_PERFORMANCE
      ) {
        this.sortingField = types.timeStamp;
      } else if (
        this.currentSubType.key == constants.SUB_TYPES.PS_CONNECTIONS ||
        this.currentSubType.key == constants.SUB_TYPES.ORA_CONNECTIONS ||
        this.currentSubType.key == constants.SUB_TYPES.MSSQL_CONNECTIONS
      ) {
        this.sortingField = types.TIMESTAMP;
      } else this.sortingField = types.timeStamp;
    },
  },

  watch: {
    currentDatabaseService(newValue, oldValue) {
      if (!newValue) {
        this.setCurrentDatabaseInstance({});
        this.setDatabaseInstances([]);
      } else {
        this.fetchDatabaseInstances();
      }
    },
    currentSubType(newValue, oldValue) {
      if (!newValue) {
        this.setCurrentDatabaseInstance({});
        this.setDatabaseInstances([]);
      } else {
        this.setDefaultSortField();
        this.fetchDatabaseInstances();
        this.items();
      }
    },
    getDurationTime(oldValue, newValue) {
      this.fetchDatabaseInstances();
      this.diskDetails();
      this.avgData();
      this.items();
    },
    currentDatabaseInstance(newValue) {
      this.diskDetails();
      this.avgData();
      this.items();
    },
  },
  async mounted() {
    this.fetchDatabaseInstances();
    this.setDefaultSortField();
    this.items();
    this.avgData();
  },
};
</script>

<style lang="scss" scoped>
.top {
  display: flex;
  flex-flow: row wrap;
  position: relative;
  margin-top: 1.6em;
}
.wrapper {
  display: flex;
  flex-flow: row wrap;
  position: relative;
  margin-top: 1.438em;
  justify-content: space-between;
}
.item-select {
  margin-top: 30px;
}
.chart-element {
  width: 49%;
  margin-bottom: 1.5em;
  margin-top: 0.125em;
  margin-left: 0.125em;
}
.chart-element-heatmap {
  height: 100%;
  margin-right: 1.375em;
  margin-bottom: 1.5em;
  margin-top: 0.125em;
  margin-left: 0.125em;
}
.chart-element-area {
  margin-bottom: 1.5em;
  margin-top: 0.125em;
  margin-left: 0.125em;
}
.icon-label {
  padding-top: 0px !important;
  color: $font-primary;
  font-size: 1.5em;
  font-weight: bold;
}
.icon-class {
  padding-top: 4px;
  @include btn-fill($font-primary);
}
.icon-class:hover {
  cursor: pointer;
  svg {
    fill: $font-secondary !important;
  }
}
.info-border {
  border-radius: 1rem;
}
.card-element {
  margin-bottom: 1.5em;
  margin-top: 0.125em;
  margin-left: 0.125em;
  border-radius: 0.5rem;
  height: 6rem;
}
.wrapper-cards-list {
  display: flex;
  flex-direction: row;
  position: relative;
  margin-top: 1.438em;
  margin-right: 1.375em;
  width: 100%;
}
.progress-card {
  flex-basis: 49%;
  overflow: auto;
}
.wrapper-card-elements {
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  flex-basis: 49%;
  margin-right: 1.6rem;
  min-height: fit-content;
}

.wrapper-card {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}
.wrapper-card-storage {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  height: 5.7rem;
}
.query-threshold-action {
  @include flex-center-justify(space-between);
  margin-bottom: 1rem;
}
:deep(.body-container) {
  display: flex;
}
:deep(.body-td-content) {
  word-break: break-all;
}
</style>
